import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { useMutation } from "@apollo/client";
import { PARTNER_BALANCE_SHEET_INDICATORS } from "../queries";
import { SAVE_PARTNER_BALANCE_SHEET_INDICATORS } from "../mutations";
import { BalanceSheetDetailsIndicatorsRowComponent } from "./BalanceSheetDetailsIndicatorsRowComponent";
import { Card, FakeText, useToast, Button } from "@totalsoft/rocket-ui";
import { TableCellWithWidth } from "components/list/TableComponentsStyled";
import { Table, TableBody, TableHead, TableRow } from "@mui/material";
import HeaderPortal from "components/layout/HeaderPortal";
import { update, assoc } from "ramda";
import { useChangeTrackingState } from "@totalsoft/change-tracking-react";
import { isDirty } from "@totalsoft/change-tracking";

export const BalanceSheetDetailsIndicatorsContainer = ({title}) => {
  const params = useParams();
  const [indicators, dirtyInfo, setIndicators, resetIndicators] = useChangeTrackingState([]);
  const [isSaving, setIsSaving] = useState(false);
  const showToast = useToast();
  const [t] = useTranslation();

  const balanceSheetId = parseInt(params?.balanceSheetId);

  const { loading } = useQueryWithSimpleErrorHandling(PARTNER_BALANCE_SHEET_INDICATORS, {
    onCompleted: data => {
      resetIndicators(data?.partnerBalanceSheetIndicators || []);
    },
    variables: { balanceSheetId: balanceSheetId },
    skip: !balanceSheetId
  });

  const [savePartnerBalanceSheetIndicators] = useMutation(SAVE_PARTNER_BALANCE_SHEET_INDICATORS, {
    onError: error => {
      showToast(error?.message, "error", { autoClose: 3000 });
      setIsSaving(false);
    },
    onCompleted: () => {
      showToast(t("BalanceSheet.BalanceSheetIndicatorsSaved"), "success", { autoClose: 3000 });
      setIsSaving(false);
    }
  });

  const handleChange = useCallback((prop, value, index) => {
    setIndicators(prev => update(index, assoc(prop, value, prev[index]), prev));
  }, [setIndicators]);

  const handleSave = useCallback(() => {
    setIsSaving(true);
    savePartnerBalanceSheetIndicators({
      variables: {
        input: indicators.map(item => ({
          balanceSheetIndicatorId: item.balanceSheetIndicatorId,
          balanceSheetId: item.balanceSheetId,
          indicatorId: item.indicatorId,
          indicatorValue: item.indicatorValue
        }))
      }
    });
  }, [indicators, savePartnerBalanceSheetIndicators]);

  return (
    <Card
      title={t("BalanceSheet.Indicators.Title")}
      actions={
        <Button
          disabled={isSaving || !isDirty(dirtyInfo)}
          onClick={handleSave}
          sx={{display: 'none'}} //manually editing indicators is not yet enabled
        >
          {isSaving ? t("Saving") : t("Save")}
        </Button>
      }
    >
      <HeaderPortal customTitle={title}></HeaderPortal>
      {loading ? (
        <FakeText lines={10} />
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCellWithWidth w={10}>{t("BalanceSheet.Indicators.OrderNo")}</TableCellWithWidth>
              <TableCellWithWidth w={18}>{t("BalanceSheet.Indicators.Name")}</TableCellWithWidth>
              <TableCellWithWidth w={20}>{t("BalanceSheet.Indicators.Value")}</TableCellWithWidth>
            </TableRow>
          </TableHead>
          <TableBody>
            {indicators.map((item, index) => (
              <BalanceSheetDetailsIndicatorsRowComponent
                key={item.configDetailId}
                item={item}
                index={index}
                handleChange={handleChange}
                handleSave={handleSave}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};

BalanceSheetDetailsIndicatorsContainer.propTypes = {
  title: PropTypes.string
};
export default BalanceSheetDetailsIndicatorsContainer;
